// ============================================
// BarraAgregandoACotizacion.js — Letrero del "MODO AGREGAR A COTIZACION" (F3.B/F3.F)
// ============================================
// Barra llamativa (acento ambar) que se muestra ARRIBA en La Tienda / Colecciones /
// Listas cuando el modo "agregar a cotizacion" esta activo. Texto:
// "Agregando productos a: [nombre]" + contador "Llevas N - $monto" (F3.F) + boton
// "Listo" que sale del modo y reabre el editor de ESA cotizacion.
//
// F3.F: el contador lee las lineas ACTUALES de la cotizacion (GET) y se refresca al
// escuchar 'beep-agregado-a-cotizacion'. Al pulsarlo, despliega un panel con la lista
// de lineas (imagen, nombre, modelo, cantidad, subtotal) y un boton "Quitar" por linea
// VISIBLE pero DESHABILITADO ("pronto" -> se activa en F3.G). F3.F NO borra nada.
//
// Se auto-oculta cuando no hay modo activo (devuelve null).

import React, { useState, useEffect, useCallback } from 'react';
import { PackagePlus, Check, ShoppingCart, ChevronDown, ChevronUp, Trash2, Package, X, Repeat } from 'lucide-react';
import { PP, COLOR_OSCURO, COLOR_GRIS, COLOR_GRIS_CLARO, COLOR_BORDE, COLOR_FONDO, API_BASE_CLI } from './helpersClientes';
import { useModoAgregar, salirModoAgregar } from '../utils/modoAgregarCotizacion';
import { fetchAuth } from '../utils/sesion';
import { useResponsive } from '../hooks/useResponsive';

// Acento de ATENCION (ambar fuerte) para que se note que estas en un modo especial.
const ATENCION = '#f59e0b';
const ATENCION_BORDE = '#b45309';

const fmtMXN = (n) => (n == null || !Number.isFinite(Number(n)))
  ? '$0.00'
  : Number(n).toLocaleString('es-MX', { style: 'currency', currency: 'MXN', minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function BarraAgregandoACotizacion() {
  const modo = useModoAgregar();
  const cotId = modo ? modo.cotizacionId : null;
  // RESPONSIVE A-1: en celular la barra se apila (izquierda en una fila, contador+Listo en
  // otra a todo el ancho) y baja fuente/padding. En ancho queda IGUAL que hoy.
  const { isMobile } = useResponsive();

  const [lineas, setLineas] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [panelAbierto, setPanelAbierto] = useState(false);
  // F3.G: quitar una linea (confirmacion inline + borrado).
  const [confirmandoId, setConfirmandoId] = useState(null);
  const [quitandoId, setQuitandoId] = useState(null);
  const [aviso, setAviso] = useState(null);

  // Lee las lineas actuales de la cotizacion (solo lectura). Best-effort: si falla,
  // deja lo que haya (el contador no rompe la barra).
  const cargar = useCallback(async () => {
    if (cotId == null) return;
    setCargando(true);
    try {
      const resp = await fetchAuth(`${API_BASE_CLI}/api/cotizaciones/${cotId}`);
      const d = await resp.json().catch(() => ({}));
      if (d && d.ok) setLineas(Array.isArray(d.productos) ? d.productos : []);
    } catch { /* deja lo que haya */ }
    finally { setCargando(false); }
  }, [cotId]);

  // Carga al entrar y cada vez que se agrega algo (evento de F3.C/F3.F).
  useEffect(() => {
    if (cotId == null) { setLineas([]); return undefined; }
    cargar();
    const refrescar = () => cargar();
    window.addEventListener('beep-agregado-a-cotizacion', refrescar);
    return () => window.removeEventListener('beep-agregado-a-cotizacion', refrescar);
  }, [cotId, cargar]);

  // Auto-limpia el aviso de feedback tras unos segundos.
  useEffect(() => {
    if (!aviso) return undefined;
    const t = setTimeout(() => setAviso(null), 2500);
    return () => clearTimeout(t);
  }, [aviso]);

  // F3.G: borra UNA linea de la cotizacion (transaccional en el backend) y refresca.
  const quitarLinea = useCallback(async (l) => {
    if (cotId == null || l == null || l.id == null) return;
    setQuitandoId(l.id);
    setConfirmandoId(null);
    try {
      const resp = await fetchAuth(`${API_BASE_CLI}/api/cotizaciones/${cotId}/productos/${l.id}`, { method: 'DELETE' });
      const d = await resp.json().catch(() => ({}));
      if (!resp.ok || !d.ok) { setAviso((d && d.error) || 'No se pudo quitar la linea'); return; }
      setLineas(prev => prev.filter(x => x.id !== l.id)); // optimista (el evento reconcilia)
      setAviso('Quitado de la cotizacion');
      try { window.dispatchEvent(new CustomEvent('beep-agregado-a-cotizacion')); } catch { /* none */ }
    } catch (e) {
      if (e.code !== 'SESION_EXPIRADA') setAviso('No se pudo quitar la linea');
    } finally {
      setQuitandoId(null);
    }
  }, [cotId]);

  if (!modo) return null;
  // Mejora #4b: en modo SUSTITUIR (sustituirLineaId) la barra cambia el texto y OCULTA el
  // contador "Llevas N" + el panel + "Listo" (es de UN solo producto); deja un "Cancelar".
  const sustituyendo = modo.sustituirLineaId != null;

  const totalItems = lineas.reduce((s, l) => s + (Math.max(1, Math.round(Number(l.cantidad) || 1))), 0);
  const totalMonto = lineas.reduce((s, l) => s + (Number(l.subtotal) || 0), 0);

  const terminar = () => {
    const id = modo.cotizacionId;
    salirModoAgregar();
    try {
      window.dispatchEvent(new CustomEvent('beep-editar-cotizacion', { detail: { cotizacionId: id } }));
    } catch { /* none */ }
  };

  return (
    <div style={{ position: 'sticky', top: 0, zIndex: 1100 }}>
      {/* BARRA AMBAR */}
      <div style={{
        display: 'flex', alignItems: isMobile ? 'stretch' : 'center', justifyContent: 'space-between',
        gap: isMobile ? 8 : 12, flexWrap: 'wrap', flexDirection: isMobile ? 'column' : 'row',
        padding: isMobile ? '8px 12px' : '10px 16px', background: ATENCION, borderBottom: `2px solid ${ATENCION_BORDE}`,
        boxShadow: '0 2px 10px rgba(180,83,9,0.25)', fontFamily: PP,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 8 : 10, minWidth: 0, color: '#fff' }}>
          {sustituyendo
            ? <Repeat size={isMobile ? 18 : 20} strokeWidth={2.4} style={{ flexShrink: 0 }} />
            : <PackagePlus size={isMobile ? 18 : 20} strokeWidth={2.4} style={{ flexShrink: 0 }} />}
          <span style={{ fontSize: isMobile ? 12 : 14, fontWeight: 800, letterSpacing: 0.2, whiteSpace: 'nowrap', flexShrink: 0 }}>
            {sustituyendo
              ? (isMobile ? 'Sustituyendo en:' : 'Sustituyendo producto en:')
              : (isMobile ? 'Agregando a:' : 'Agregando productos a:')}
          </span>
          <span style={{
            fontSize: isMobile ? 12 : 14, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            background: 'rgba(255,255,255,0.22)', padding: '3px 10px', borderRadius: 999,
            maxWidth: isMobile ? '100%' : '40vw', flex: isMobile ? 1 : '0 1 auto', minWidth: 0,
          }}>{modo.nombre || 'la cotizacion'}</span>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0, width: isMobile ? '100%' : 'auto' }}>
          {/* CONTADOR "Llevas N - $monto" -> abre/cierra el panel (oculto al sustituir) */}
          {!sustituyendo && (
          <button
            type="button"
            onClick={() => setPanelAbierto(o => !o)}
            aria-expanded={panelAbierto}
            title="Ver lo agregado a la cotizacion"
            style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 7, cursor: 'pointer', fontFamily: PP,
              background: 'rgba(255,255,255,0.22)', color: '#fff', border: '1px solid rgba(255,255,255,0.5)',
              padding: '8px 12px', borderRadius: 8, fontSize: 13, fontWeight: 800, minHeight: 40, whiteSpace: 'nowrap',
              flex: isMobile ? 1 : '0 0 auto',
            }}
          >
            <ShoppingCart size={16} strokeWidth={2.2} />
            Llevas {totalItems} · {fmtMXN(totalMonto)}
            {panelAbierto ? <ChevronUp size={15} strokeWidth={2.4} /> : <ChevronDown size={15} strokeWidth={2.4} />}
          </button>
          )}

          {/* LISTO (agregar) / CANCELAR (sustituir) — ambos salen del modo y vuelven al cotizador */}
          <button
            type="button"
            onClick={terminar}
            style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              background: '#fff', color: ATENCION_BORDE, border: 'none', cursor: 'pointer',
              padding: '9px 16px', borderRadius: 8, fontSize: 14, fontWeight: 800, fontFamily: PP,
              minHeight: 40, boxShadow: '0 1px 3px rgba(0,0,0,0.15)', whiteSpace: 'nowrap',
              flex: isMobile && sustituyendo ? 1 : '0 0 auto',
            }}
          >
            {sustituyendo
              ? <><X size={17} strokeWidth={2.6} /> Cancelar</>
              : <><Check size={17} strokeWidth={2.6} /> Listo</>}
          </button>
        </div>
      </div>

      {/* PANEL DESPLEGABLE — lista de lineas (solo lectura en F3.F; oculto al sustituir) */}
      {panelAbierto && !sustituyendo && (
        <div style={{
          background: '#fff', borderBottom: `1px solid ${COLOR_BORDE}`,
          boxShadow: '0 6px 16px rgba(0,0,0,0.10)', fontFamily: PP,
        }}>
          {aviso && (
            <div style={{ padding: '8px 16px', fontSize: 12, fontWeight: 700, color: COLOR_OSCURO, background: COLOR_FONDO, borderBottom: `1px solid ${COLOR_BORDE}` }}>
              {aviso}
            </div>
          )}
          <div style={{ maxHeight: 320, overflowY: 'auto' }}>
            {cargando && lineas.length === 0 && (
              <div style={{ padding: 20, textAlign: 'center', color: COLOR_GRIS_CLARO, fontSize: 13 }}>Cargando…</div>
            )}
            {!cargando && lineas.length === 0 && (
              <div style={{ padding: 20, textAlign: 'center', color: COLOR_GRIS, fontSize: 13 }}>
                Aun no has agregado productos a esta cotizacion.
              </div>
            )}
            {lineas.map((l, i) => {
              const cant = Math.max(1, Math.round(Number(l.cantidad) || 1));
              return (
                <div key={l.id != null ? l.id : i} style={{
                  display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px',
                  borderBottom: `1px solid ${COLOR_BORDE}`,
                }}>
                  <span style={{
                    flexShrink: 0, width: 44, height: 44, borderRadius: 8, background: COLOR_FONDO,
                    border: `1px solid ${COLOR_BORDE}`, overflow: 'hidden',
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    {l.imagen_url
                      ? <img src={l.imagen_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain' }} onError={(e) => { e.target.style.display = 'none'; }} />
                      : <Package size={18} strokeWidth={1.5} color={COLOR_GRIS_CLARO} />}
                  </span>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: COLOR_OSCURO, lineHeight: 1.3 }}>{l.nombre}</div>
                    {l.modelo && <div style={{ fontSize: 12, color: '#6d28d9', fontWeight: 800, fontFamily: 'monospace', marginTop: 1 }}>{l.modelo}</div>}
                    <div style={{ fontSize: 12, color: COLOR_GRIS_CLARO, marginTop: 1 }}>
                      {cant} × {fmtMXN(l.precio_final != null ? l.precio_final : l.precio_unitario)}
                    </div>
                  </div>
                  <div style={{ flexShrink: 0, textAlign: 'right', fontSize: 13, fontWeight: 700, color: COLOR_OSCURO, minWidth: 64 }}>
                    {fmtMXN(l.subtotal)}
                  </div>
                  {/* QUITAR (F3.G) — confirmacion inline antes de borrar. */}
                  {quitandoId === l.id ? (
                    <span style={{ flexShrink: 0, fontSize: 11, fontWeight: 700, color: COLOR_GRIS_CLARO, minWidth: 64, textAlign: 'right' }}>Quitando…</span>
                  ) : confirmandoId === l.id ? (
                    <span style={{ flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      <button
                        type="button"
                        onClick={() => quitarLinea(l)}
                        style={{
                          display: 'inline-flex', alignItems: 'center', gap: 3, background: '#e05c5c', color: '#fff',
                          border: 'none', padding: '6px 9px', borderRadius: 7, fontSize: 11, fontWeight: 800,
                          fontFamily: PP, cursor: 'pointer',
                        }}
                      ><Trash2 size={12} strokeWidth={2.4} /> Si, quitar</button>
                      <button
                        type="button"
                        onClick={() => setConfirmandoId(null)}
                        style={{
                          background: '#fff', color: COLOR_GRIS, border: `1px solid ${COLOR_BORDE}`,
                          padding: '6px 9px', borderRadius: 7, fontSize: 11, fontWeight: 700, fontFamily: PP, cursor: 'pointer',
                        }}
                      >No</button>
                    </span>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setConfirmandoId(l.id)}
                      disabled={l.id == null}
                      title="Quitar de la cotizacion"
                      style={{
                        flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: 4,
                        background: '#fff', color: '#e05c5c', border: `1px solid ${COLOR_BORDE}`,
                        padding: '6px 8px', borderRadius: 7, fontSize: 11, fontWeight: 700, fontFamily: PP,
                        cursor: l.id == null ? 'not-allowed' : 'pointer', opacity: l.id == null ? 0.5 : 1,
                      }}
                    >
                      <Trash2 size={13} strokeWidth={2} /> Quitar
                    </button>
                  )}
                </div>
              );
            })}
          </div>
          {/* TOTAL */}
          {lineas.length > 0 && (
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '10px 16px', background: COLOR_FONDO, borderTop: `1px solid ${COLOR_BORDE}`,
            }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: COLOR_GRIS }}>
                {totalItems} producto{totalItems === 1 ? '' : 's'}
              </span>
              <span style={{ fontSize: 15, fontWeight: 800, color: COLOR_OSCURO }}>{fmtMXN(totalMonto)}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
